import { Component } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { JwtHelper } from '../../../services/core/jwt-helper.service';
import { map } from 'rxjs/operators';
import { AuthService } from 'src/app/services/core/auth.service';
import { AppUtil } from '../../../utils/app.util';
import { AuthSession, decodeJWT } from '@aws-amplify/core';
import { SsoType } from '../../shared/types';
import { Capacitor } from '@capacitor/core';

interface CredentialTokenParams {
	access_token: string;
	id_token: string;
	token_type: string;
	expires_in: number;
}

/** SSO Redirection component */
@Component({
	selector: 'app-sso-redirection',
	templateUrl: './sso-redirection.component.html',
	styleUrls: ['./sso-redirection.component.scss'],
})
export class SsoRedirectionComponent {
	/** IDToken from SSO */
	idToken: any;
	/** decoded token object  */
	tokenObject: any;
	/** Flag to check if device is mobile */
	isMobile = Capacitor.isNativePlatform();
	/**
	 * SSO Redirection component Constructor
	 * @param activatedRoute - Angular Activated Route
	 * @param jwtService - JWT Service
	 * @param router - Angular Router
	 * @param authService - Auth Service
	 */
	constructor(
		private activatedRoute: ActivatedRoute,
		private router: Router,
		private authService: AuthService
	) {
		this.getQueryParams();
	}

	/**
	 * get Token from SSO provider
	 */
	getQueryParams() {
		if (AppUtil.isIE()) {
			try {
				let ssoRedirectUrl = localStorage.getItem('ssoRedirectUrl');
				ssoRedirectUrl = ssoRedirectUrl.split('#')[1];
				const searchParams = new URLSearchParams(ssoRedirectUrl);
				const params: CredentialTokenParams = {
					access_token: searchParams.get('access_token'),
					id_token: searchParams.get('id_token'),
					token_type: searchParams.get('token_type'),
					expires_in: Number(searchParams.get('expires_in')) || 3600,
				};
				this.processIdToken(params);
			} catch (Exception) {
				console.error(
					`[${SsoRedirectionComponent.name}][${this.getQueryParams.name}][1]`,
					'An error occurred while processing sso in IE : ',
					Exception
				);
				this.router.navigate(['sso/saml/sso-error'], { replaceUrl: true });
			}
		} else {
			this.activatedRoute.fragment
				.pipe(
					map(fragment => new URLSearchParams(fragment)),
					map(params => ({
						access_token: params.get('access_token'),
						id_token: params.get('id_token'),
						token_type: params.get('token_type'),
						expires_in: Number(params.get('expires_in')) || 3600,
					}))
				)
				.subscribe((params: CredentialTokenParams) => {
					console.log(params);
					if (!(params && params.id_token && params.access_token && params.token_type && params.expires_in)) {
						console.error(
							`[${SsoRedirectionComponent.name}][${this.getQueryParams.name}][2]`,
							'An error occurred while processing sso - params not found'
						);
						this.router.navigate(['sso/saml/sso-error'], { replaceUrl: true });
					} else {
						this.processIdToken(params);
					}
				});
		}
	}

	async processIdToken(tokenParams: CredentialTokenParams) {
		const authSession = SsoRedirectionComponent.getAuthSession(tokenParams.access_token, tokenParams.id_token);
		await this.authService.updateAwsCredentialsForSsoSp(authSession);
		this.router.navigate(['dashboard'], { replaceUrl: true });
	}

	public static getAuthSession(accessToken: string, idToken: string) {
		const decodedAccessToken = decodeJWT(accessToken);
		const decodedIdToken = decodeJWT(idToken);
		console.log(
			'tokens:',
			{ accessToken, idToken },
			{ decodedAccessToken, decodedIdToken },
			decodedIdToken.payload.exp * 1000 <= new Date().getTime()
		);
		if (new Date().getTime() <= decodedIdToken.payload.exp * 1000) {
			sessionStorage.setItem(
				'auth',
				JSON.stringify({
					accessToken: { jwtToken: accessToken, payload: decodedAccessToken },
					idToken: { jwtToken: idToken, payload: decodedIdToken },
				})
			);
			sessionStorage.setItem('sso', 'true');
			sessionStorage.setItem('sso-type', SsoType.SPInitiated);
			sessionStorage.setItem('sso-tokens', JSON.stringify({ accessToken, idToken }));

			const authSession: AuthSession = {
				credentials: {
					accessKeyId: '',
					secretAccessKey: '',
					expiration: new Date(),
					sessionToken: '',
				},
				identityId: '',
				tokens: {
					accessToken: decodedAccessToken,
					idToken: decodedIdToken,
				},
				userSub: decodedIdToken.payload.sub,
			};
			return authSession;
		}
		throw Error('Token Expired');
	}
}
